/**
 * Centralized asset loading cache for deduplicating concurrent requests.
 * Prevents duplicate network requests when React components mount simultaneously.
 * Relies on internal pendingMap for concurrent deduplication and browser HTTP cache for already-loaded assets.
 */

type Loader<T> = () => Promise<T>;

// Single internal map shared across all asset types (JSON, CSS, JS)
// URLs are unique, so a single map is sufficient
const assetCache = new Map<string, Promise<unknown>>();

/**
 * Deduplicates asset loading by sharing promises for concurrent requests.
 * Multiple concurrent requests for the same URL share a single promise.
 */
export async function loadAssetWithCache<T>(
    url: string,
    loader: Loader<T>
): Promise<T> {
    // Check if there's already an in-flight request for this URL
    const pending = assetCache.get(url);
    if (pending) {
        return pending as Promise<T>;
    }

    // Create new load promise
    const loadPromise = loader();

    // Store pending request
    assetCache.set(url, loadPromise);

    return await loadPromise;
}

/**
 * Clears the asset cache. Useful for testing to ensure test isolation.
 */
export function clearAssetCache(): void {
    assetCache.clear();
}
